export const CATEGORY_AWARE_QUADRATURE_START_YEAR = 2026;

export type QuadratureContext = {
  season?: string | null;
  speciesId?: number | string | null;
};

export type QuadraturePolicy = {
  version: 'legacy-v1' | 'category-v2';
  includeCategory: boolean;
};

type SettlementQuadratureProduct = {
  variety?: string | null;
  caliber?: string | null;
  color?: string | null;
  format?: string | null;
  label?: string | null;
  unit_weight?: number | string | null;
  category?: string | null;
};

type ShipmentQuadratureProduct = {
  labeled_variety?: string | null;
  variety?: string | null;
  caliber?: string | null;
  color?: string | null;
  box_format?: string | null;
  box_label?: string | null;
  unit_weight?: number | string | null;
  box_weight?: number | string | null;
  category?: string | null;
};

const normalizeText = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const normalizeNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  return Number.isFinite(number) ? number.toString() : normalizeText(value);
};

const getSeasonStartYear = (season: string | null | undefined) => {
  const match = String(season ?? '')
    .trim()
    .match(/^(\d{4})/);
  if (!match) return null;
  const year = Number(match[1]);
  return Number.isFinite(year) ? year : null;
};

export const getQuadraturePolicy = (
  context: QuadratureContext
): QuadraturePolicy => {
  const seasonStartYear = getSeasonStartYear(context.season);
  const includeCategory =
    seasonStartYear !== null &&
    seasonStartYear >= CATEGORY_AWARE_QUADRATURE_START_YEAR;

  return includeCategory
    ? { version: 'category-v2', includeCategory: true }
    : { version: 'legacy-v1', includeCategory: false };
};

export const normalizeQuadratureReference = normalizeText;

export const buildSettlementQuadratureProductKey = (
  detail: SettlementQuadratureProduct,
  context: QuadratureContext
) => {
  const values = [
    normalizeText(detail.variety),
    normalizeText(detail.caliber),
    normalizeText(detail.color),
    normalizeText(detail.format),
    normalizeText(detail.label),
    normalizeNumber(detail.unit_weight),
  ];

  if (getQuadraturePolicy(context).includeCategory) {
    values.push(normalizeText(detail.category));
  }

  return values.join('|');
};

export const buildShipmentQuadratureProductKey = (
  box: ShipmentQuadratureProduct,
  context: QuadratureContext
) => {
  const values = [
    normalizeText(box.labeled_variety) || normalizeText(box.variety),
    normalizeText(box.caliber),
    normalizeText(box.color),
    normalizeText(box.box_format),
    normalizeText(box.box_label),
    normalizeNumber(box.unit_weight ?? box.box_weight),
  ];

  if (getQuadraturePolicy(context).includeCategory) {
    values.push(normalizeText(box.category));
  }

  return values.join('|');
};
